import React, { useEffect, useMemo, useRef } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
}

interface ServiceSpend {
  serviceName: string;
  amount: number;
}

const SpendByServiceChart = ({ caption }: WidgetProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // Static data - to be replaced with API data later
  const serviceSpendData: ServiceSpend[] = useMemo(
    () => [
      { serviceName: "Janitorial", amount: 125000 },
      { serviceName: "HVAC", amount: 85000 },
      { serviceName: "Plumbing", amount: 62000 },
      { serviceName: "Electrical", amount: 58000 },
      { serviceName: "Landscaping", amount: 45000 },
      { serviceName: "Pest Control", amount: 32000 },
      { serviceName: "Security", amount: 28000 },
      { serviceName: "Painting", amount: 22000 },
    ],
    [],
  );

  useEffect(() => {
    if (!svgRef.current || serviceSpendData.length === 0) return;

    const margin = { top: 20, right: 30, bottom: 80, left: 70 };
    const width = 500 - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X scale
    const x = d3
      .scaleBand()
      .domain(serviceSpendData.map((d) => d.serviceName))
      .range([0, width])
      .padding(0.3);

    // Y scale
    const maxAmount = d3.max(serviceSpendData, (d) => d.amount) || 0;
    const y = d3
      .scaleLinear()
      .domain([0, maxAmount * 1.1])
      .range([height, 0]);

    // Color scale
    const colorScale = d3
      .scaleLinear<string>()
      .domain([0, serviceSpendData.length - 1])
      .range([colors.kleenway.green, "#2d5a3d"]);

    // Add X axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("font-size", "11px")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    // Add Y axis
    svg
      .append("g")
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickFormat((d) => `$${(d as number) / 1000}k`),
      )
      .selectAll("text")
      .style("font-size", "12px");

    // Add bars with grow-from-baseline animation
    svg
      .selectAll(".bar")
      .data(serviceSpendData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.serviceName) || 0)
      .attr("y", height)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("fill", (d, i) => colorScale(i))
      .style("opacity", 0.9)
      .on("mouseover", function (event, d) {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");
        // Show tooltip
        const tooltip = svg
          .append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${x(d.serviceName)! + x.bandwidth() / 2},${y(d.amount) - 10})`);

        tooltip
          .append("rect")
          .attr("x", -50)
          .attr("y", -25)
          .attr("width", 100)
          .attr("height", 20)
          .attr("fill", "rgba(0,0,0,0.8)")
          .attr("rx", 4);

        tooltip
          .append("text")
          .attr("text-anchor", "middle")
          .attr("y", -10)
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text(`$${d.amount.toLocaleString()}`);
      })
      .on("mouseout", function (this: SVGRectElement) {
        d3.select(this).style("opacity", 0.9);
        svg.selectAll(".tooltip").remove();
      })
      .transition()
      .duration(700)
      .delay((_, i) => i * 60)
      .ease(d3.easeQuadOut)
      .attr("y", (d) => y(d.amount))
      .attr("height", (d) => height - y(d.amount));

    // Add Y axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", colors.grey.dark)
      .text("Spend ($)");
  }, [serviceSpendData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          <StaticDataBadge>Static Data</StaticDataBadge>
        </HeaderRow>
        <ChartContainer>
          <svg ref={svgRef}></svg>
        </ChartContainer>
      </Stack>
    </WidgetContainer>
  );
};

export default SpendByServiceChart;

const WidgetContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;
